// GeneralSettings.tsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SecurityIcon from "@mui/icons-material/Security";
import VisibilityIcon from "@mui/icons-material/Visibility"; // Changed from EditIcon for read-only mode
import { Application } from "@microsoft/microsoft-graph-types";
import { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface GeneralSettingsProps {
  app: Application;
  modifiedApp: Application;
  setModifiedApp: React.Dispatch<React.SetStateAction<Application>>;
  spLoading: boolean;
  servicePrincipalId: string | null;
  tenantId: string;

  handleCopyToClipboard: (text: string) => void;
  setClientCredsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // REMOVED: updateApplication, graphAccessToken, onAppUpdated - not needed in read-only mode
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  app,
  modifiedApp,
  setModifiedApp,
  spLoading,
  servicePrincipalId,
  tenantId,
  handleCopyToClipboard,
  setClientCredsModalOpen,
}) => {
  const hasWebUris = Boolean(modifiedApp.web?.redirectUris?.length);
  const hasSpaUris = Boolean(modifiedApp.spa?.redirectUris?.length);
  const [showSecurityTips, setShowSecurityTips] = useState(false);
  const [showIdentifierGuide, setShowIdentifierGuide] = useState(false);
  const [showIntegrationTips, setShowIntegrationTips] = useState(false); 

  // REMOVED: All platform management state and functions
  // REMOVED: addingPlatform, newPlatformUri, platformError, selectedPlatformType
  // REMOVED: handleAddPlatform, handleCancelAddPlatform

  return (
    <>
      {/* 1) IDENTIFIERS */}
      <Accordion 
        defaultExpanded
        sx={{
          border: '1px solid #e8eaf6', // Light indigo border
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Indigo gradient
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5', // Indigo background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <VpnKeyIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593", // Dark indigo text
                letterSpacing: "0.01em"
              }}
            >
              Identifiers
            </Typography>            <Chip 
              size="small"
              label="Read-Only" // Updated label to indicate read-only mode
              sx={{ 
                ml: 2,
                backgroundColor: '#e0e0e0', // Gray background for read-only
                color: '#616161',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}> {/* Light indigo background */}
          {/* Keep existing content but update container styling */}
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}          <Typography variant="body1" sx={{ mb: 2 }}>
            These unique identifiers are used in authentication flows and API calls to reference your application.
          </Typography>

          {/* READ-ONLY Notice */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Read-Only Mode:</strong> Application identifiers and configuration are displayed for reference only. 
              No modifications can be made in this view.
            </Typography>
          </Alert>

          {/* Visual explanation of identifiers */}
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            <VpnKeyIcon sx={{ color: '#607d8b', mr: 2, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">
                <strong>Object ID:</strong> The unique identifier for this application in your Entra ID directory.<br />
                <strong>Application ID (clientId):</strong> Used in authentication requests and code. This is your "client_id".<br />
                <strong>Service Principal ID:</strong> The ID of the service instance in your tenant.<br />
                <strong>Tenant ID:</strong> The directory ID where this application is registered.
              </Typography>
              
              {/* Usage guidance link */}
              <Button 
                onClick={() => setShowIdentifierGuide(!showIdentifierGuide)}
                startIcon={<InfoOutlinedIcon fontSize="small" />}
                size="small"
                sx={{ mt: 1.5, textTransform: 'none' }}
                color="info"
              >
                {showIdentifierGuide ? 'Hide Usage Guide' : 'Show Usage Guide'}
              </Button>
            </Box>
          </Box>

          {/* Collapsible usage guide */}
          {showIdentifierGuide && (
            <Box sx={{ 
              mb: 3,
              p: 1.5, 
              backgroundColor: '#e3f2fd', 
              borderLeft: '4px solid #2196f3',
              borderRadius: '0 4px 4px 0'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                When to use each identifier:
              </Typography>
              <Typography variant="body2">
                • Use <strong>Object ID</strong> when managing the application via Graph API<br />
                • Use <strong>Application ID</strong> in authentication requests and code<br />
                • Use <strong>Service Principal ID</strong> for role assignments and tenant-specific configurations<br />
                • Use <strong>Tenant ID</strong> to specify the Entra ID directory
              </Typography>
            </Box>
          )}

            {/* Actual identifiers with copy buttons */}
            <Box sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: 1
            }}>
              <Stack spacing={2}>                {/* Display Name field - READ-ONLY */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Display Name:
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',
                        sm: '70%'
                      },
                      maxWidth: '70%' 
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.875rem',
                        flexGrow: 1,
                        color: '#424242' // Darker color to indicate read-only
                      }}
                    >
                      {app.displayName || "N/A"}
                    </Typography>
                    <VisibilityIcon fontSize="small" sx={{ opacity: 0.6, color: 'text.secondary', ml: 1 }} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Object ID:
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {app.id || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(app.id || "")} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Application ID (clientId):
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {app.appId || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(app.appId || "")} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {spLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">Fetching Service Principal ID...</Typography>
                  </Stack>
                ) : servicePrincipalId ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                      Service Principal ID:
                    </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: '#f5f5f5', 
                        p: 1, 
                        borderRadius: 1, 
                        width: {
                          xs: '60%',  // On extra small screens
                          sm: '70%'   // On small screens and larger
                        },
                        maxWidth: '70%' 
                      }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          flexGrow: 1 // This will make the text take available space
                        }}
                      >
                        {servicePrincipalId}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyToClipboard(servicePrincipalId)} sx={{ ml: 'auto' }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="error">
                    Service Principal not found
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Tenant ID:
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {tenantId || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(tenantId)} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>                </Box>
              </Stack>
              
              {/* REMOVED: Update Display Name button */}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 2) REDIRECT URIs */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M17 7h-4v2h4c1.1 0 2 .9 2 2s-.9 2-2 2h-3v2h3c2.21 0 4-1.79 4-4s-1.79-4-4-4zm-8 8h2V9H9v2H7v2h2v2zm-2-8v2h5V7H7z"/>
              </svg>
            </Box>            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Redirect URIs
            </Typography>
            <Chip 
              size="small"
              label="Read-Only" 
              sx={{ 
                ml: 2,
                backgroundColor: '#e0e0e0',
                color: '#616161',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}            <Typography variant="body1" sx={{ mb: 2 }}>
              These are the configured redirect URIs for your application. In read-only mode, you can view and copy these URIs but cannot modify them.
            </Typography>
            
            {/* Visual explanation of URI types with expandable security tips */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              backgroundColor: '#f8f8ff', 
              border: '1px solid #ddddff', 
              borderRadius: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ color: '#3f51b5', mr: 2, mt: 0.5 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 7h-4v2h4c1.1 0 2 .9 2 2s-.9 2-2 2h-3v2h3c2.21 0 4-1.79 4-4s-1.79-4-4-4zm-8 8h2V9H9v2H7v2h2v2zm-2-8v2h5V7H7z"/>
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <strong>Web Redirect URIs:</strong> For traditional web applications that run on a server (e.g., <code>https://myapp.com/auth/callback</code>)<br />
                    <strong>SPA Redirect URIs:</strong> For JavaScript single-page applications running in a browser (also HTTPS URLs)<br />
                    <strong>Mobile/Native URIs:</strong> For mobile or desktop apps (e.g., <code>myapp://callback</code> or <code>https://login.microsoftonline.com/common/oauth2/nativeclient</code>)
                  </Typography>
                  
                  {/* Security tips link */}
                  <Button 
                    onClick={() => setShowSecurityTips(!showSecurityTips)}
                    startIcon={<SecurityIcon fontSize="small" />}
                    size="small"
                    sx={{ mt: 1.5, textTransform: 'none' }}
                    color="warning"
                  >
                    {showSecurityTips ? 'Hide Security Best Practices' : 'Show Security Best Practices'}
                  </Button>
                </Box>
              </Box>
              
              {/* Collapsible security tips */}
              {showSecurityTips && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: '#fff8e1', 
                  borderLeft: '4px solid #ffb300',
                  borderRadius: '0 4px 4px 0'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                    Security Best Practices:
                  </Typography>
                  <Typography variant="body2">
                    • Always use HTTPS URLs for production applications<br />
                    • Be as specific as possible (include the full path)<br />
                    • Never use wildcards in production redirect URIs<br />
                    • Localhost URIs should only be used for development
                  </Typography>
                </Box>
              )}
            </Box>            {/* Platform Status - READ-ONLY */}
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#e8f0fe', 
              border: '1px solid #c0d6ff', 
              borderRadius: 1
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {hasWebUris && hasSpaUris ? (
                  <>
                    This application supports <strong>both Web and SPA</strong> platforms.
                  </>
                ) : hasWebUris ? (
                  <>
                    This application is configured as a <strong>Web</strong> app.
                  </>
                ) : hasSpaUris ? (
                  <>
                    This application is configured as an <strong>SPA</strong> app.
                  </>
                ) : (
                  <>
                    This application has <strong>no platforms configured</strong>.
                  </>
                )}
              </Typography>
            </Box>{/* REMOVED: Add Missing Platform Section */}            {/* WEB redirect URIs - READ-ONLY */}
            {hasWebUris && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                  Web Redirect URIs
                </Typography>
                {modifiedApp.web?.redirectUris?.map((uri, index) => (
                  <Box
                    key={`web-${index}`}
                    sx={{ 
                      mb: 1,
                      p: 2,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                        flexGrow: 1,
                        mr: 2
                      }}
                    >
                      {uri}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyToClipboard(uri)}
                      sx={{ flexShrink: 0 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                
                {/* REMOVED: Add/Apply buttons */}
              </>
            )}            {/* SPA redirect URIs - READ-ONLY */}
            {hasSpaUris && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, mt: 3 }}>
                  SPA Redirect URIs
                </Typography>
                {modifiedApp.spa?.redirectUris?.map((uri, index) => (
                  <Box
                    key={`spa-${index}`}
                    sx={{ 
                      mb: 1,
                      p: 2,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                        flexGrow: 1,
                        mr: 2
                      }}
                    >
                      {uri}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyToClipboard(uri)}
                      sx={{ flexShrink: 0 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                
                {/* REMOVED: Add/Apply buttons */}
              </>
            )}            
            {/* No URIs configured message */}
            {!hasWebUris && !hasSpaUris && (
              <Box sx={{ 
                mt: 2,
                p: 3,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                textAlign: 'center',
                border: '2px dashed #ddd'
              }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No redirect URIs configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application has no redirect URIs configured for any platform.
                </Typography>
              </Box>
            )}
            
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 3) SECRETS & CERTIFICATES */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Secrets & Certificates
            </Typography>
            <Chip 
              size="small"
              label="View Only" 
              sx={{ 
                ml: 2,
                backgroundColor: '#e0e0e0',
                color: '#616161',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}          <Typography variant="body1" sx={{ mb: 2 }}>
            View the client secrets and certificates for this application. These credentials allow the application to authenticate directly without a user.
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 2, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9', 
            borderRadius: 1
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <VisibilityIcon color="primary" />
              <Typography variant="body2" fontWeight="medium">
                View application credentials and security configuration
              </Typography>
            </Stack>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={() => setClientCredsModalOpen(true)}
              sx={{
                fontWeight: 500,
                px: 2
              }}
            >
              View Credentials
            </Button>
          </Box>
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* 4) SUPPORTED ACCOUNT TYPES */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <PeopleIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Supported Account Types
            </Typography>            <Chip 
              size="small"
              label="Read-Only" 
              sx={{ 
                ml: 2,
                backgroundColor: '#e0e0e0',
                color: '#616161',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}            <Typography variant="body1" sx={{ mb: 2 }}>
              This shows which account types can sign in to this application. This configuration affects compatibility with other tenants and the Microsoft identity platform.
            </Typography>
            
            {/* Visual explanation of account types */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              backgroundColor: '#f0fff0', 
              border: '1px solid #c8e6c9', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center'
            }}>
              <PeopleIcon sx={{ color: '#4caf50', mr: 2 }} />
              <Typography variant="body2">
                <strong>Single tenant:</strong> Only users in your organization<br />
                <strong>Multi-tenant:</strong> Users in any Entra ID organization<br />
                <strong>+Personal accounts:</strong> Also includes Microsoft accounts like Outlook.com
              </Typography>
            </Box>            
            {/* READ-ONLY Account Type Display */}
            <Box sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Current Configuration:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                {modifiedApp.signInAudience === 'AzureADMyOrg' 
                  ? 'Accounts in this organizational directory only (Single tenant)' 
                  : modifiedApp.signInAudience === 'AzureADMultipleOrgs'
                  ? 'Accounts in any organizational directory (Multi-tenant)'
                  : modifiedApp.signInAudience === 'AzureADandPersonalMicrosoftAccount'
                  ? 'Accounts in any organizational directory + personal Microsoft accounts'
                  : modifiedApp.signInAudience === 'PersonalMicrosoftAccount'
                  ? 'Personal Microsoft accounts only'
                  : 'Unknown configuration'}
              </Typography>
            </Box>

            {/* REMOVED: FormControl Select and Apply Changes button */}
          </Box>
        </AccordionDetails>
      </Accordion>      
    </>
  );
};

export default GeneralSettings;
